/**
 * Converts a string into a URL-friendly slug.
 * @param {string} text
 * @returns {string}
 */
export function slugify(text) {
  if (!text || typeof text !== "string") return "";

  return text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove accents/diacritics
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // remove non-alphanumeric chars except space and hyphen
    .trim()
    .replace(/\s+/g, "-") // replace spaces with single hyphen
    .replace(/-+/g, "-"); // remove duplicate hyphens
}

/**
 * Generates an SEO-friendly slug for a video: {artist}-{title}-music-video-{videoId}
 * @param {string} artist
 * @param {string} title
 * @param {string|number} videoId
 * @returns {string}
 */
export function generateVideoSlug(artist, title, videoId) {
  const artistSlug = slugify(artist);
  const titleSlug = slugify(title);
  const prefix = [artistSlug, titleSlug].filter(Boolean).join("-");

  if (!prefix) {
    return videoId ? `music-video-${videoId}` : "";
  }

  return `${prefix}-music-video-${videoId}`;
}

/**
 * Returns the full relative URL for a video page: /video/{artist}-{title}-music-video-{videoId}
 * @param {Object|string|number} video
 * @returns {string}
 */
export function getVideoUrl(video) {
  if (!video) return "/video";

  if (typeof video === "string" || typeof video === "number") {
    const str = String(video);
    if (str.startsWith("/video/")) return str;
    return `/video/${str}`;
  }

  const id = video.id;
  const slug = generateVideoSlug(video.artist, video.title, id);
  return `/video/${slug || id}`;
}

/**
 * Extracts the video ID from a slug or raw ID string.
 * Supports {artist}-{title}-music-video-{videoId}, legacy {videoId}, and full URLs.
 * @param {string} slugOrUrl
 * @returns {string}
 */
export function extractVideoId(slugOrUrl) {
  if (!slugOrUrl || typeof slugOrUrl !== "string") return "";

  // Remove query params and hash
  let cleaned = slugOrUrl.split("?")[0].split("#")[0];

  // If full URL or path, get the last segment
  if (cleaned.includes("/")) {
    const segments = cleaned.split("/").filter(Boolean);
    cleaned = segments[segments.length - 1] || "";
  }

  const parts = cleaned.split("-");
  return parts[parts.length - 1] || cleaned;
}

/**
 * Generates an SEO-friendly slug for a playlist: {playlistName}-playlist-{playlistId}
 * @param {string} name
 * @param {string|number} playlistId
 * @returns {string}
 */
export function generatePlaylistSlug(name, playlistId) {
  const nameSlug = slugify(name);

  if (!nameSlug) {
    return playlistId ? `playlist-${playlistId}` : "";
  }

  return `${nameSlug}-playlist-${playlistId}`;
}

/**
 * Returns the full relative URL for a playlist page: /playlist/{playlistName}-playlist-{playlistId}
 * @param {Object|string|number} playlist
 * @param {Object} [queryParams] - e.g. { video: 0, autoplay: 1 }
 * @returns {string}
 */
export function getPlaylistUrl(playlist, queryParams = {}) {
  if (!playlist) return "/playlist";

  let slug = "";
  if (typeof playlist === "string" || typeof playlist === "number") {
    const str = String(playlist);
    slug = str.startsWith("/playlist/") ? str.replace("/playlist/", "") : str;
  } else {
    const id = playlist.id || playlist.playlist_id;
    slug = generatePlaylistSlug(playlist.name || playlist.title, id) || String(id);
  }

  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, val]) => {
    if (val !== undefined && val !== null) {
      searchParams.set(key, val);
    }
  });

  const queryString = searchParams.toString();
  return `/playlist/${slug}${queryString ? `?${queryString}` : ""}`;
}

/**
 * Extracts the playlist ID from a slug or raw ID string.
 * Supports {name}-playlist-{playlistId}, legacy {playlistId}, and full URLs.
 * @param {string} slugOrUrl
 * @returns {string}
 */
export function extractPlaylistId(slugOrUrl) {
  if (!slugOrUrl || typeof slugOrUrl !== "string") return "";

  // Remove query params and hash
  let cleaned = slugOrUrl.split("?")[0].split("#")[0];

  // If full URL or path, get the last segment
  if (cleaned.includes("/")) {
    const segments = cleaned.split("/").filter(Boolean);
    cleaned = segments[segments.length - 1] || "";
  }

  const parts = cleaned.split("-");
  return parts[parts.length - 1] || cleaned;
}
